export type MfaChannel = 'sms' | 'email' | 'totp';

export interface MfaChallenge {
  /** Always sms, email, totp — full method set for UX. */
  supportedChannels: MfaChannel[];
  /** Subset valid for this login on `POST /api/auth/mfa/verify`. */
  channels: MfaChannel[];
  phoneMasked?: string;
  emailMasked?: string;
  smsDispatched?: boolean;
  emailDispatched?: boolean;
}

/** Plain session after primary auth when MFA is not required. */
export type LoginSuccess = { accessToken: string };

/** Must enroll at least one factor; JWT is enrollment-only (short-lived). */
export type LoginMfaSetup = {
  mfaSetupRequired: true;
  accessToken: string;
};

/** Payload when entering enrollment-only flow (same token as `LoginMfaSetup`). */
export interface MfaSetupRequiredPayload {
  accessToken: string;
}

/** MFA verify step after primary auth or `POST /api/auth/mfa/step-up/start`. */
export type LoginMfaVerify = {
  mfaRequired: true;
  /** True when the challenge was started via logged-in step-up (refresh `mfa_at`), not primary login. */
  mfaStepUp?: boolean;
  mfaContextToken: string;
  mfaChallenge: MfaChallenge;
};

export type LoginResponse = LoginSuccess | LoginMfaSetup | LoginMfaVerify;

export interface MfaVerifyRequest {
  mfaContextToken: string;
  channel: MfaChannel;
  code: string;
}

export interface MfaVerifySuccess {
  accessToken: string;
}

/** `POST /api/auth/mfa/dispatch` — merge into `mfaChallenge` after user picks SMS or email. */
export interface MfaDispatchResponse {
  phoneMasked?: string;
  emailMasked?: string;
  smsDispatched?: boolean;
  emailDispatched?: boolean;
  supportedChannels?: MfaChannel[];
  channels?: MfaChannel[];
}

/** Client state between primary auth and MFA verify. */
export interface MfaRequiredPayload {
  mfaContextToken: string;
  mfaChallenge: MfaChallenge;
}

export interface SmsEmailEnrollStartResponse {
  enrollToken: string;
  phoneMasked?: string;
  emailMasked?: string;
}

export interface TotpEnrollStartResponse {
  enrollToken: string;
  binding: Record<string, unknown>;
}

/**
 * Enrollment verify always returns `success`.
 * `accessToken` is included only when the request used the enrollment-only JWT (`mfa_enrollment_only`);
 * then replace the stored token and treat the user as fully signed in (includes `mfa_at`).
 */
export type MfaEnrollVerifyResponse = { success: true; accessToken?: string };

export interface MfaRemoveRequest {
  channel: MfaChannel;
}

export interface MfaRemoveResponse {
  success: boolean;
}

/** `GET /api/auth/mfa/status` — policy + enrolled methods (source of truth vs JWT). */
export interface MfaStatusResponse {
  required: boolean;
  supportedChannels: MfaChannel[];
  configuredChannels: MfaChannel[];
}

/** Handles older responses that omit `supportedChannels`. */
export function normalizeMfaChallenge(
  raw: Partial<MfaChallenge> & { channels?: MfaChannel[] }
): MfaChallenge {
  const channels = raw.channels ?? [];
  const supportedChannels: MfaChannel[] =
    raw.supportedChannels && raw.supportedChannels.length > 0
      ? raw.supportedChannels
      : (['sms', 'email', 'totp'] as MfaChannel[]);
  return {
    supportedChannels,
    channels,
    phoneMasked: raw.phoneMasked,
    emailMasked: raw.emailMasked,
    smsDispatched: raw.smsDispatched,
    emailDispatched: raw.emailDispatched,
  };
}
